import { css, type SerializedStyles } from "@emotion/react";
import type { ComponentPropsWithoutRef } from "react";

type ButtonVariant = "primary" | "secondary" | "gold" | "outline" | "danger" | "link";
type ButtonSize = "default" | "sm" | "xs" | "xl";

interface ButtonProps extends ComponentPropsWithoutRef<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

const btnBase = css`
  padding: 14px 28px;
  border-radius: 12px;
  border: none;
  font-family: var(--font-heading);
  font-size: var(--text-base);
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px var(--shadow-medium);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px var(--shadow-dark);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    padding: 10px 18px;
    font-size: var(--text-sm);
  }
`;

const btnPrimary = css`
  background: var(--color-primary);
  color: #FFFFFF;
  box-shadow: 0 4px 16px rgba(192, 57, 43, 0.35);

  &:hover:not(:disabled) {
    box-shadow: 0 8px 24px rgba(192, 57, 43, 0.45);
  }
`;

const btnSecondary = css`
  background: var(--color-navy);
  color: #FFFFFF;

  &:hover:not(:disabled) {
    background: var(--color-navy-mid);
  }
`;

const btnGold = css`
  background: linear-gradient(135deg, var(--color-gold), var(--color-gold-dark));
  color: var(--color-navy);
  font-weight: 900;
  box-shadow: 0 4px 16px rgba(241, 196, 15, 0.4);

  &:hover:not(:disabled) {
    box-shadow: 0 8px 24px rgba(241, 196, 15, 0.5);
  }
`;

const btnOutline = css`
  background: transparent;
  color: var(--color-primary);
  border: 2px solid var(--color-primary);
  box-shadow: none;

  &:hover:not(:disabled) {
    background: rgba(192, 57, 43, 0.05);
    box-shadow: none;
  }
`;

const btnDanger = css`
  background: var(--error-bg);
  color: var(--error-text);

  &:hover:not(:disabled) {
    background: rgba(231, 76, 60, 0.2);
  }
`;

const btnSm = css`
  font-size: var(--text-sm);
  padding: 8px 16px;
`;

const btnXs = css`
  font-size: var(--text-xs);
  padding: 6px 12px;
`;

const btnXl = css`
  font-size: 5rem;
  padding: 16px 32px;

  @media (max-width: 640px) {
    font-size: 3rem;
    padding: 12px 24px;
  }
`;

const btnLinkDiscrete = css`
  background: none;
  border: none;
  color: var(--text-secondary);
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  opacity: 0.7;
  transition: opacity 0.15s ease;
  box-shadow: none;

  &:hover {
    opacity: 1;
    transform: none;
    box-shadow: none;
  }
`;

const variantStyles: Record<ButtonVariant, SerializedStyles> = {
  primary: btnPrimary,
  secondary: btnSecondary,
  gold: btnGold,
  outline: btnOutline,
  danger: btnDanger,
  link: btnLinkDiscrete,
};

const sizeStyles: Record<ButtonSize, SerializedStyles | null> = {
  default: null,
  sm: btnSm,
  xs: btnXs,
  xl: btnXl,
};

export function Button({
  variant = "primary",
  size = "default",
  type = "button",
  className,
  ...rest
}: ButtonProps) {
  const styles =
    variant === "link"
      ? [btnLinkDiscrete]
      : [btnBase, variantStyles[variant], sizeStyles[size]].filter(Boolean);

  return <button type={type} css={styles} className={className} {...rest} />;
}
